import * as THREE from 'three';
import config from '@/game/hexmap/config';
import { GROUND_Y, groundPoint, groundTileKey } from '@/game/hexmap/view/groundPick';

/**
 * Analytic click targets for the instanced layers, in place of invisible hit
 * meshes: units by a ray–sphere test against where they're drawn, own
 * buildings by the tile under the cursor. Hits are ordered for the click
 * rules (`clickPick.ts`): every unit hit before any building hit, and a
 * building hit before the board's (`TilePicker`, at the ground itself), so
 * nearer handlers can stop the event.
 */

const UNIT_Y = config.hexMap.tile.thickness;
/** A unit's click target: a sphere this big, this high over its feet. */
const HIT_RADIUS = 0.45;
const HIT_Y = 0.35;
/** Height of a unit's middle over the board: its click sphere's centre, and where box-select projects it. */
export const UNIT_CENTER_Y = UNIT_Y + HIT_Y;
/** How far in front of the ground under the cursor building and unit hits are put at most. */
const BUILDING_LEAD = 5e-4;
const UNIT_LEAD = 1e-3;

/** A drawn unit as its click target sees it. */
export interface Pickable {
  id: string;
  x: number;
  z: number;
  /** Its logical tile and whether it's on its way somewhere (`unitTakesClick`). */
  tile: string;
  moving: boolean;
}

/** What a hit on the unit picker stands for, for the buildings' click rules. */
export interface UnitHit {
  unitTile: string;
  unitMoving: boolean;
}

const sphere = new THREE.Sphere(new THREE.Vector3(), HIT_RADIUS);
const hitPoint = new THREE.Vector3();

/**
 * The units' click target: one ray–sphere test per drawn unit; a hit's
 * `instanceId` indexes `pickables.current`. A hit never lies past the ground
 * under the cursor. `userData.unitHit(instanceId)` describes a hit.
 */
export function createUnitPicker(pickables: { current: readonly Pickable[] }): THREE.Object3D {
  const picker = new THREE.Object3D();
  picker.raycast = (raycaster, intersects) => {
    const ground = groundPoint(raycaster.ray);
    pickables.current.forEach((p, instanceId) => {
      sphere.center.set(p.x, UNIT_CENTER_Y, p.z);
      if (!raycaster.ray.intersectSphere(sphere, hitPoint)) return;
      let distance = raycaster.ray.origin.distanceTo(hitPoint);
      if (ground) distance = Math.min(distance, ground.t - UNIT_LEAD);
      if (distance < raycaster.near || distance > raycaster.far) return;
      intersects.push({ distance, point: hitPoint.clone(), object: picker, instanceId });
    });
  };
  picker.userData.unitHit = (instanceId: number): UnitHit | null => {
    const p = pickables.current[instanceId];
    return p ? { unitTile: p.tile, unitMoving: p.moving } : null;
  };
  return picker;
}

/** A unit picker hit's description, from any intersection (null for anything else). */
export function unitHitOf(hit: { object: THREE.Object3D; instanceId?: number }): UnitHit | null {
  const describe = hit.object.userData.unitHit as ((i: number) => UnitHit | null) | undefined;
  return describe && hit.instanceId !== undefined ? describe(hit.instanceId) : null;
}

/**
 * Own buildings' click target: hit when the ground under the cursor is one
 * of `tiles.current` (tile key → building id), just in front of the ground.
 */
export function createBuildingPicker(tiles: {
  current: ReadonlyMap<string, string>;
}): THREE.Object3D {
  const picker = new THREE.Object3D();
  picker.raycast = (raycaster, intersects) => {
    const hit = groundPoint(raycaster.ray);
    if (!hit || !tiles.current.has(groundTileKey(hit.x, hit.z))) return;
    const distance = hit.t - BUILDING_LEAD;
    if (distance < raycaster.near || distance > raycaster.far) return;
    intersects.push({ distance, point: new THREE.Vector3(hit.x, GROUND_Y, hit.z), object: picker });
  };
  return picker;
}
